import React, { useState, useEffect, useRef } from 'react';
import { 
  Users, Plus, Search, Edit2, Trash2, Loader2, X, Phone, Mail, Calendar,
  Clock, CheckCircle, XCircle, Eye, Download, User, Upload, Image
} from 'lucide-react';
import { leadershipAPI, adminLeadershipAppealsAPI } from '../services/api';
import Pagination from '../components/Pagination';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const STATUS_CONFIG = {
  pending: { label: 'Jarayonda', color: '#f59e0b', bg: '#fef3c7', icon: Clock },
  accepted: { label: 'Qabul qilindi', color: '#22c55e', bg: '#dcfce7', icon: CheckCircle },
  rejected: { label: 'Rad qilindi', color: '#ef4444', bg: '#fee2e2', icon: XCircle },
};

const LeadershipPage = () => {
  const [activeTab, setActiveTab] = useState('leaders');
  const [leaders, setLeaders] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLeader, setFilterLeader] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showAppealModal, setShowAppealModal] = useState(false);
  const [editingLeader, setEditingLeader] = useState(null);
  const [selectedAppeal, setSelectedAppeal] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    full_name: '',
    position: '',
    reception_days: '',
    phone: '',
    email: '',
    order: 0,
  });
  const [updateData, setUpdateData] = useState({ status: '', admin_comment: '' });

  // Rasm URL ni olish
  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith('http')) return photo;
    return `${API_URL}${photo}`;
  };

  // Rasm tanlash
  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Rasmni o'chirish
  const handlePhotoRemove = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (activeTab === 'leaders') {
      fetchLeaders();
    } else {
      fetchAppeals();
    }
  }, [activeTab, filterStatus, filterLeader]);

  const fetchLeaders = async () => {
    setLoading(true);
    try {
      const response = await leadershipAPI.getAll();
      setLeaders(response.data.results || response.data || []);
    } catch (error) {
      console.error('Xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAppeals = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterLeader) params.leader = filterLeader;
      
      const response = await adminLeadershipAppealsAPI.getAll(params);
      setAppeals(response.data.appeals || []);
      setStats(response.data.stats || null);
    } catch (error) {
      console.error('Xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = new FormData();
      submitData.append('full_name', formData.full_name);
      submitData.append('position', formData.position);
      submitData.append('is_active', 'true');
      submitData.append('order', formData.order || 0);
      
      // Bo'sh bo'lmagan maydonlarni qo'shish
      if (formData.reception_days) {
        submitData.append('reception_days', formData.reception_days);
      }
      if (formData.phone) {
        submitData.append('phone', formData.phone);
      }
      if (formData.email) {
        submitData.append('email', formData.email);
      }
      
      if (photoFile) {
        submitData.append('photo', photoFile);
      }
      
      if (editingLeader) {
        await leadershipAPI.update(editingLeader.id, submitData);
      } else {
        await leadershipAPI.create(submitData);
      }
      setShowModal(false);
      resetForm();
      fetchLeaders();
    } catch (error) {
      console.error('Xato:', error);
      console.error('Response:', error.response?.data);
      const errorMsg = error.response?.data 
        ? JSON.stringify(error.response.data) 
        : 'Xatolik yuz berdi';
      alert(errorMsg);
    }
  };

  const handleDelete = async (leader) => {
    if (window.confirm(`"${leader.full_name}" ni o'chirmoqchimisiz?`)) {
      try {
        await leadershipAPI.delete(leader.id);
        fetchLeaders();
      } catch (error) {
        alert('Xatolik yuz berdi');
      }
    }
  };

  const handleUpdateAppeal = async () => {
    try {
      await adminLeadershipAppealsAPI.update(selectedAppeal.id, updateData);
      setShowAppealModal(false);
      fetchAppeals();
    } catch (error) {
      alert('Xatolik yuz berdi');
    }
  };

  const openEditModal = (leader) => {
    setEditingLeader(leader);
    setFormData({
      full_name: leader.full_name,
      position: leader.position,
      reception_days: leader.reception_days || '',
      phone: leader.phone || '',
      email: leader.email || '',
      order: leader.order || 0,
    });
    // Mavjud rasmni preview qilish
    if (leader.photo) {
      setPhotoPreview(getPhotoUrl(leader.photo));
    } else {
      setPhotoPreview(null);
    }
    setPhotoFile(null);
    setShowModal(true);
  };

  const openAppealModal = (appeal) => {
    setSelectedAppeal(appeal);
    setUpdateData({ status: appeal.status, admin_comment: appeal.admin_comment || '' });
    setShowAppealModal(true);
  };

  const resetForm = () => {
    setEditingLeader(null);
    setFormData({
      full_name: '',
      position: '',
      reception_days: '',
      phone: '',
      email: '',
      order: 0,
    });
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const exportToExcel = () => {
    const data = appeals.map(a => ({
      'ID': a.id,
      'Mavzu': a.subject,
      'Xabar': a.message,
      'Rahbar': a.leadership_name,
      'Lavozim': a.leadership_position,
      'Talaba': a.student_name,
      'Guruh': a.group_name || '-',
      'Holat': STATUS_CONFIG[a.status]?.label,
      'Javob': a.admin_comment || '-',
      'Sana': new Date(a.created_at).toLocaleDateString('uz-UZ')
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Murojaatlar');
    XLSX.writeFile(wb, 'rahbariyat_murojaatlari.xlsx');
  };

  const filteredLeaders = leaders.filter(l =>
    l.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.position.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><Users size={28} /> Rahbariyat</h1>
          <p>Rahbariyat va murojaatlarni boshqarish</p>
        </div>
        {activeTab === 'leaders' ? (
          <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
            <Plus size={18} />
            Yangi rahbar
          </button>
        ) : (
          <button className="btn btn-success" onClick={exportToExcel} disabled={appeals.length === 0}>
            <Download size={18} />
            Excel
          </button>
        )}
      </header>

      {/* Tabs */}
      <div className="leadership-tabs">
        <button 
          className={`tab-btn ${activeTab === 'leaders' ? 'active' : ''}`}
          onClick={() => setActiveTab('leaders')}
        >
          <Users size={18} />
          Rahbariyat ({leaders.length})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'appeals' ? 'active' : ''}`}
          onClick={() => setActiveTab('appeals')}
        >
          <Mail size={18} />
          Murojaatlar {stats?.pending > 0 && <span className="badge">{stats.pending}</span>}
        </button>
      </div>

      {activeTab === 'leaders' ? (
        <>
          {/* Search */}
          <div className="filters-row">
            <div className="search-bar">
              <Search size={20} />
              <input 
                type="text" 
                placeholder="Qidirish..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Leaders Grid */}
          {loading ? (
            <div className="loading-container">
              <Loader2 className="spinner" size={40} />
            </div>
          ) : filteredLeaders.length === 0 ? (
            <div className="empty-state">
              <Users size={48} />
              <p>Rahbariyat topilmadi</p>
            </div>
          ) : (
            <div className="leaders-grid">
              {filteredLeaders.map(leader => (
                <div key={leader.id} className="leader-card">
                  <div className="leader-photo">
                    {leader.photo ? (
                      <img src={leader.photo} alt={leader.full_name} />
                    ) : (
                      <User size={48} />
                    )}
                  </div>
                  <div className="leader-info">
                    <h3>{leader.full_name}</h3>
                    <p className="leader-position">{leader.position}</p>
                    {leader.reception_days && (
                      <div className="leader-detail">
                        <Calendar size={14} />
                        <span>{leader.reception_days}</span>
                      </div>
                    )}
                    {leader.phone && (
                      <div className="leader-detail">
                        <Phone size={14} />
                        <span>{leader.phone}</span>
                      </div>
                    )}
                    {leader.email && (
                      <div className="leader-detail">
                        <Mail size={14} />
                        <span>{leader.email}</span>
                      </div>
                    )}
                  </div>
                  <div className="leader-actions">
                    <button className="icon-btn" onClick={() => openEditModal(leader)}>
                      <Edit2 size={16} />
                    </button>
                    <button className="icon-btn danger" onClick={() => handleDelete(leader)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Stats */}
          {stats && (
            <div className="appeals-stats">
              <div className="stat-card total">
                <span className="stat-number">{stats.total}</span>
                <span className="stat-label">Jami</span>
              </div>
              <div className="stat-card pending">
                <Clock size={24} />
                <div>
                  <span className="stat-number">{stats.pending}</span>
                  <span className="stat-label">Jarayonda</span>
                </div>
              </div>
              <div className="stat-card accepted">
                <CheckCircle size={24} />
                <div>
                  <span className="stat-number">{stats.accepted}</span>
                  <span className="stat-label">Qabul qilindi</span>
                </div>
              </div>
              <div className="stat-card rejected">
                <XCircle size={24} />
                <div>
                  <span className="stat-number">{stats.rejected}</span>
                  <span className="stat-label">Rad qilindi</span>
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="filters-row">
            <select 
              className="filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">Barcha holatlar</option>
              <option value="pending">⏳ Jarayonda</option>
              <option value="accepted">✅ Qabul qilindi</option>
              <option value="rejected">❌ Rad qilindi</option>
            </select>
            <select 
              className="filter-select"
              value={filterLeader}
              onChange={(e) => setFilterLeader(e.target.value)}
            >
              <option value="">Barcha rahbarlar</option>
              {leaders.map(l => (
                <option key={l.id} value={l.id}>{l.full_name}</option>
              ))}
            </select>
          </div>

          {/* Appeals List */}
          {loading ? (
            <div className="loading-container">
              <Loader2 className="spinner" size={40} />
            </div>
          ) : appeals.length === 0 ? (
            <div className="empty-state">
              <Mail size={48} />
              <p>Murojaatlar topilmadi</p>
            </div>
          ) : (
            <div className="appeals-list">
              {appeals.map(appeal => {
                const statusConfig = STATUS_CONFIG[appeal.status];
                const StatusIcon = statusConfig.icon;
                return (
                  <div key={appeal.id} className="appeal-card">
                    <div className="appeal-header">
                      <div className="appeal-status" style={{ background: statusConfig.bg, color: statusConfig.color }}>
                        <StatusIcon size={14} />
                        {statusConfig.label}
                      </div>
                      <span className="appeal-date">
                        {new Date(appeal.created_at).toLocaleDateString('uz-UZ')}
                      </span>
                    </div>
                    <h4>{appeal.subject}</h4>
                    <p className="appeal-message">{appeal.message.substring(0, 150)}...</p>
                    <div className="appeal-meta">
                      <div className="meta-item">
                        <User size={14} />
                        <span>{appeal.student_name}</span>
                      </div>
                      <div className="meta-item">
                        <Users size={14} />
                        <span>{appeal.leadership_name}</span>
                      </div>
                    </div>
                    <div className="appeal-actions">
                      <button className="btn btn-primary btn-sm" onClick={() => openAppealModal(appeal)}>
                        <Eye size={16} />
                        Ko'rish
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Leader Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingLeader ? 'Rahbarni tahrirlash' : 'Yangi rahbar'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Rasm yuklash */}
                <div className="form-group">
                  <label>Rasm</label>
                  <div className={`photo-upload-section ${photoPreview ? 'has-photo' : ''}`}>
                    <div className="photo-preview">
                      {photoPreview ? (
                        <img src={photoPreview} alt="Preview" />
                      ) : (
                        <User size={48} />
                      )}
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="photo-upload-input"
                      accept="image/*"
                      onChange={handlePhotoSelect}
                    />
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button 
                        type="button" 
                        className="photo-upload-btn"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload size={16} />
                        {photoPreview ? "O'zgartirish" : 'Rasm yuklash'}
                      </button>
                      {photoPreview && (
                        <button 
                          type="button" 
                          className="photo-remove-btn"
                          onClick={handlePhotoRemove}
                        >
                          <X size={14} />
                          O'chirish
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>F.I.SH *</label>
                    <input
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Lavozimi *</label>
                    <input
                      type="text"
                      value={formData.position}
                      onChange={(e) => setFormData({...formData, position: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Qabul kunlari</label>
                  <input
                    type="text"
                    value={formData.reception_days}
                    onChange={(e) => setFormData({...formData, reception_days: e.target.value})}
                    placeholder="Masalan: Dushanba, Chorshanba 09:00-12:00"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Telefon</label>
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+998 XX XXX XX XX"
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Tartib raqami</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.order}
                    onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Bekor qilish
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingLeader ? 'Saqlash' : 'Qo\'shish'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Appeal Modal */}
      {showAppealModal && selectedAppeal && (
        <div className="modal-overlay" onClick={() => setShowAppealModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Murojaat #{selectedAppeal.id}</h2>
              <button className="modal-close" onClick={() => setShowAppealModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="appeal-detail">
                <div className="detail-row">
                  <label>Rahbar:</label>
                  <strong>{selectedAppeal.leadership_name} - {selectedAppeal.leadership_position}</strong>
                </div>
                <div className="detail-row">
                  <label>Talaba:</label>
                  <strong>{selectedAppeal.student_name} ({selectedAppeal.group_name})</strong>
                </div>
                <div className="detail-row">
                  <label>Mavzu:</label>
                  <strong>{selectedAppeal.subject}</strong>
                </div>
                <div className="detail-section">
                  <label>Xabar:</label>
                  <p>{selectedAppeal.message}</p>
                </div>
              </div>
              
              <div className="update-section">
                <h4>Holatni yangilash</h4>
                <div className="status-buttons">
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <button
                        key={key}
                        className={`status-btn ${updateData.status === key ? 'active' : ''}`}
                        style={{ '--btn-color': config.color, '--btn-bg': config.bg }}
                        onClick={() => setUpdateData({...updateData, status: key})}
                      >
                        <Icon size={18} />
                        {config.label}
                      </button>
                    );
                  })}
                </div>
                <div className="form-group">
                  <label>Javob</label>
                  <textarea
                    value={updateData.admin_comment}
                    onChange={(e) => setUpdateData({...updateData, admin_comment: e.target.value})}
                    placeholder="Javob yozing..."
                    rows={3}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAppealModal(false)}>
                Bekor qilish
              </button>
              <button className="btn btn-primary" onClick={handleUpdateAppeal}>
                <CheckCircle size={18} />
                Saqlash
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadershipPage;
